/*
 * Author: zoesvision.com
 * Purpose: Provide isolated Embla navigation and editor integration for z-split-carousel.
 */

if (!customElements.get('z-split-carousel')) {
  customElements.define('z-split-carousel', class ZSplitCarousel extends HTMLElement {
    connectedCallback() {
      if (this.isInitialized) return;

      this.viewport = this.querySelector('[data-z-viewport]');
      this.slides = Array.from(this.querySelectorAll('[data-z-split-slide]'));
      if (!this.viewport || !this.slides.length) return;

      this.isInitialized = true;
      this.abortController = new AbortController();
      this.indicatorContainers = Array.from(this.querySelectorAll('[data-z-indicators]'));
      this.previousButtons = Array.from(this.querySelectorAll('[data-z-previous]'));
      this.nextButtons = Array.from(this.querySelectorAll('[data-z-next]'));
      this.liveRegion = this.querySelector('[data-z-live-region]');

      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.bindEvents();
      this.initializeEmbla();
    }

    disconnectedCallback() {
      this.destroy();
    }

    bindEvents() {
      const signal = this.abortController.signal;
      this.addEventListener('keydown', this.handleKeydown, { signal });
      this.previousButtons.forEach((button) => {
        button.addEventListener('click', () => this.navigate('previous'), { signal });
      });
      this.nextButtons.forEach((button) => {
        button.addEventListener('click', () => this.navigate('next'), { signal });
      });
      document.addEventListener('shopify:block:select', this.handleBlockSelect, { signal });
    }

    initializeEmbla() {
      if (typeof globalThis.EmblaCarousel !== 'function') {
        this.dependencyAttempts = (this.dependencyAttempts || 0) + 1;
        if (this.dependencyAttempts <= 120 && this.isConnected) {
          this.dependencyFrame = requestAnimationFrame(() => this.initializeEmbla());
        }
        return;
      }

      this.embla = globalThis.EmblaCarousel(this.viewport, {
        loop: this.dataset.zLoop === 'true',
        align: 'start',
        containScroll: 'trimSnaps',
        slidesToScroll: 1,
        /* zoesvision.com | 2026-08-14 | Purpose: Use controls for fade navigation instead of horizontal dragging. */
        watchDrag: false,
        watchResize: false,
        duration: 0
      });

      this.handleReInit = this.handleReInit.bind(this);
      this.embla.on('reInit', this.handleReInit);
      this.selectedIndex = 0;
      this.buildIndicators();
      this.syncState();
      this.dataset.zReady = 'true';
    }

    buildIndicators() {
      if (!this.indicatorContainers.length || !this.embla) return;

      this.indicatorContainers.forEach((container) => container.replaceChildren());
      this.indicators = this.indicatorContainers.map((container) => {
        /* zoesvision.com | 2026-08-15 | Purpose: Keep every slide indicator available when the fade layout overlaps slide geometry. */
        return this.slides.map((unusedSlide, index) => {
          const label = this.slides[index]?.dataset.zIndicatorLabel || `Slide ${index + 1}`;
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'z-split-carousel__indicator';
          button.textContent = label;
          button.setAttribute('aria-label', `Go to ${label}`);
          button.addEventListener('click', () => {
            this.selectSlide(index, true);
          }, { signal: this.abortController.signal });
          container.append(button);
          return button;
        });
      });
    }

    navigate(direction) {
      const offset = direction === 'previous' ? -1 : 1;
      this.selectSlide(this.selectedIndex + offset, true);
    }

    handleReInit() {
      this.slides = Array.from(this.querySelectorAll('[data-z-split-slide]'));
      this.selectedIndex = Math.min(this.selectedIndex, this.slides.length - 1);
      this.buildIndicators();
      this.syncState();
    }

    /* zoesvision.com | 2026-08-15 | Purpose: Drive fade selection from real slide indexes because overlapped slides have no distinct Embla snaps. */
    selectSlide(index, announce = false) {
      if (!this.slides.length) return;

      const lastIndex = this.slides.length - 1;
      if (this.dataset.zLoop === 'true') index = (index + this.slides.length) % this.slides.length;
      else index = Math.max(0, Math.min(index, lastIndex));
      if (index === this.selectedIndex) return;

      this.selectedIndex = index;
      this.embla?.scrollTo(index, true);
      this.syncState();
      if (announce) this.announceSelection();
    }

    syncState() {
      const selectedIndex = this.selectedIndex;
      const indicatorCount = this.slides.length;
      /* zoesvision.com | 2026-08-08 | Purpose: Convert the configured odd indicator limit into an equal neighbor radius. */
      const maximumVisible = Number(this.dataset.zMaxIndicators) || 3;
      const nearbyRadius = Math.floor(maximumVisible / 2);
      this.indicators?.forEach((indicatorSet) => {
        indicatorSet.forEach((indicator, index) => {
          if (index === selectedIndex) {
            indicator.setAttribute('aria-current', 'true');
            indicator.removeAttribute('data-z-nearby');
            return;
          }

          indicator.removeAttribute('aria-current');
          /* zoesvision.com | 2026-08-08 | Purpose: Keep only the configured active neighborhood visible, including loop boundaries. */
          const distance = Math.abs(index - selectedIndex);
          const loopDistance = this.dataset.zLoop === 'true'
            ? Math.min(distance, indicatorCount - distance)
            : distance;
          if (loopDistance <= nearbyRadius) indicator.dataset.zNearby = 'true';
          else indicator.removeAttribute('data-z-nearby');
        });
      });

      const loops = this.dataset.zLoop === 'true';
      this.previousButtons.forEach((button) => { button.disabled = !loops && selectedIndex === 0; });
      this.nextButtons.forEach((button) => { button.disabled = !loops && selectedIndex === this.slides.length - 1; });
      this.slides.forEach((slide, index) => this.setSlideActive(slide, index === selectedIndex));
    }

    setSlideActive(slide, isActive) {
      slide.setAttribute('aria-hidden', String(!isActive));
      /* zoesvision.com | 2026-08-16 | Purpose: Keep nested z-media playback aligned with the selected split-carousel slide. */
      slide.querySelectorAll('z-media').forEach((media) => {
        if (media.closest('z-split-carousel') !== this) return;
        media.dispatchEvent(new CustomEvent('z:media-carousel-active', {
          detail: { active: isActive }
        }));
      });
      slide.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
        if (!isActive && element.tabIndex !== -1) {
          element.dataset.zSplitCarouselTabindex = element.getAttribute('tabindex') ?? '';
          element.tabIndex = -1;
        } else if (isActive && Object.hasOwn(element.dataset, 'zSplitCarouselTabindex')) {
          const previous = element.dataset.zSplitCarouselTabindex;
          if (previous === '') element.removeAttribute('tabindex');
          else element.setAttribute('tabindex', previous);
          delete element.dataset.zSplitCarouselTabindex;
        }
      });
    }

    announceSelection() {
      if (!this.liveRegion) return;
      const index = this.selectedIndex;
      const label = this.slides[index]?.dataset.zIndicatorLabel || `Slide ${index + 1}`;
      this.liveRegion.textContent = `${label}, ${index + 1} of ${this.slides.length}`;
    }

    handleKeydown(event) {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.target.matches('input, select, textarea')) return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

      event.preventDefault();
      this.navigate(event.key === 'ArrowLeft' ? 'previous' : 'next');
    }

    handleBlockSelect(event) {
      const selectedSlide = this.slides.find((slide) => slide.dataset.zBlockId === event.detail?.blockId);
      if (!selectedSlide) return;

      this.selectSlide(this.slides.indexOf(selectedSlide));
    }

    destroy() {
      cancelAnimationFrame(this.dependencyFrame);
      this.abortController?.abort();
      this.slides?.forEach((slide) => {
        this.setSlideActive(slide, true);
        slide.removeAttribute('aria-hidden');
      });
      this.querySelectorAll('z-media').forEach((media) => {
        if (media.closest('z-split-carousel') !== this) return;
        media.dispatchEvent(new CustomEvent('z:media-carousel-release'));
      });
      this.embla?.destroy();
      this.indicatorContainers?.forEach((container) => container.replaceChildren());
      this.embla = null;
      this.isInitialized = false;
      this.dataset.zReady = 'false';
    }
  });
}
